import { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { fetchListItems } from "../graphClient";
import { formatDate, formatCostCents } from "../lib/format";
import SearchInput from "./SearchInput";

// Internal field names of register items that the search input scans.
// Schemas vary per register (Nairana uses Location, Defender uses
// LocationStorage), so we include both — non-existent fields are silently
// skipped per-item.
const REGISTER_SEARCH_FIELDS = [
  "Title",
  "Location",
  "LocationStorage",
  "Brand",
  "Model",
  "Serial",
  "Supplier",
  "Notes",
];

function formatFieldValue(value, type) {
  if (value === null || value === undefined || value === "") return null;
  if (type === "number") return formatCostCents(value);
  if (type === "dateTime") return formatDate(value);
  return String(value);
}

function ItemCard({ item, schema }) {
  const title = item.fields.Title || "Untitled";
  const category = item.fields.Category;
  const notes = item.fields.Notes;

  const detailFields = schema.filter(
    (f) => f.name !== "Title" && f.name !== "Category" && f.name !== "Notes"
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="font-semibold text-slate-900 text-base">{title}</h3>
        {category && (
          <span className="shrink-0 text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-700">
            {category}
          </span>
        )}
      </div>
      <div className="space-y-1 text-sm text-slate-600">
        {detailFields.map((f) => {
          const v = formatFieldValue(item.fields[f.name], f.type);
          if (!v) return null;
          return (
            <div key={f.name} className="flex justify-between gap-2">
              <span>{f.label}</span>
              <span
                className={`font-medium text-slate-900 text-right max-w-[60%] ${
                  f.type === "number" ? "tabular-nums" : ""
                }`}
              >
                {v}
              </span>
            </div>
          );
        })}
        {notes && (
          <p className="text-slate-500 text-xs mt-2 pt-2 border-t border-slate-200">
            {notes}
          </p>
        )}
      </div>
    </div>
  );
}

export default function RegisterListView({
  listName,
  title,
  schema,
  onBack,
  onAdd,
}) {
  const { instance, accounts } = useMsal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchListItems(instance, accounts[0], listName);
      data.sort((a, b) =>
        (a.fields.Title || "").localeCompare(b.fields.Title || "")
      );
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listName]);

  // Reset the search when navigating between registers.
  useEffect(() => {
    setQuery("");
  }, [listName]);

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const f = it.fields || {};
      return REGISTER_SEARCH_FIELDS.some((name) => {
        const v = f[name];
        return v != null && String(v).toLowerCase().includes(q);
      });
    });
  }, [items, query]);

  const hasQuery = query.trim().length > 0;
  const matched = searched.length;
  const total = items.length;

  return (
    <div>
      {/* Sticky header — back nav, title, Add button, and search input.
          Pinned below the global navy bar (~56px) during scroll. */}
      <div className="sticky top-[68px] z-[5] pt-2 pb-3 bg-slate-50 border-b border-slate-200/70 mb-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={onBack}
              className="text-sm text-slate-500 hover:text-slate-700 px-2 h-9 rounded-lg hover:bg-slate-100 transition-colors shrink-0"
              aria-label="Back to register list"
            >
              &larr; Back
            </button>
            <h2 className="text-lg font-semibold text-slate-900 tracking-tight truncate">
              {title}
            </h2>
          </div>
          <button
            onClick={onAdd}
            className="text-sm bg-[#1B3A5C] hover:bg-[#0E243B] text-white px-3 h-9 rounded-lg font-medium transition-colors shrink-0"
          >
            + Add item
          </button>
        </div>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search by name, location, brand, model, serial, notes…"
          ariaLabel={`Search ${title}`}
        />
        <p className="text-[11px] text-slate-400 mt-1.5 px-1 tabular-nums">
          {loading
            ? "Loading items…"
            : hasQuery
            ? `Showing ${matched} of ${total}`
            : `${total} ${total === 1 ? "item" : "items"}`}
        </p>
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-12 text-center text-slate-400">
          Loading items…
        </div>
      )}

      {error && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6 text-center">
          <p className="text-slate-900 text-sm font-medium mb-2">
            Failed to load items
          </p>
          <p className="text-slate-500 text-xs mb-4 break-all">{error}</p>
          <button
            onClick={load}
            className="text-sm bg-[#1B3A5C] hover:bg-[#0E243B] text-white px-4 py-2 rounded-xl font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-8 text-center">
          <p className="text-slate-500 text-sm mb-4">No items yet</p>
          <button
            onClick={onAdd}
            className="text-sm bg-[#1B3A5C] hover:bg-[#0E243B] text-white px-4 py-2 rounded-xl font-medium transition-colors"
          >
            + Add the first item
          </button>
        </div>
      )}

      {!loading && !error && items.length > 0 && hasQuery && matched === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-8 text-center">
          <p className="text-sm text-slate-500">
            No items match &ldquo;
            <span className="font-medium text-slate-800">{query}</span>&rdquo;
          </p>
          <button
            type="button"
            onClick={() => setQuery("")}
            className="mt-3 text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
          >
            Clear search
          </button>
        </div>
      )}

      {!loading && !error && matched > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {searched.map((item) => (
            <ItemCard key={item.id} item={item} schema={schema} />
          ))}
        </div>
      )}
    </div>
  );
}
